import { Text, View } from "@react-pdf/renderer";
import { centsToDollars, parsePrice } from "helpers/price";
import { SpecialtyListDictCheckout } from "types/Provider/Provider";

import { styles } from "../../styles";

interface IBreakdownRow {
  specialty: string;
  insurance_rate: number;
  insurance_pays: number;
  you_pay: number;
  cptCodes?: string[];
}

interface INonCoveredProcedure {
  id?: string;
  cptDescription: string;
  amountInCents: number;
}

export function BreakdownTable({
  breakdown,
  nonCoveredProcedures = [],
  clearPrice,
  isSelfPayRate = false,
}: {
  breakdown: IBreakdownRow[];
  nonCoveredProcedures?: INonCoveredProcedure[];
  clearPrice: number;
  isSelfPayRate?: boolean;
}) {
  const totals = {
    insurance_rate:
      breakdown?.reduce(
        (sum: number, row: IBreakdownRow) => sum + (row.insurance_rate || 0),
        0
      ) || 0,
    insurance_pays:
      breakdown?.reduce(
        (sum: number, row: IBreakdownRow) => sum + (row.insurance_pays || 0),
        0
      ) || 0,
    you_pay:
      breakdown?.reduce(
        (sum: number, row: IBreakdownRow) => sum + (row.you_pay || 0),
        0
      ) || 0,
  };

  if (!breakdown || breakdown.length === 0) {
    return null;
  }

  return (
    <View
      style={{
        marginBottom: 10,
        marginLeft: 50,
        marginRight: 50,
        display: "flex",
        width: "100%",
        flexDirection: "column",
      }}
      wrap={false}
    >
      <Text
        style={[
          styles.payerTitleBold,
          { marginBottom: 10, textAlign: "center" },
        ]}
      >
        Payment Breakdown
      </Text>
      <View
        style={{
          ...styles.table,
          width: 510,
          marginLeft: 50,
          marginRight: 50,
        }}
      >
        <View
          style={[
            styles.row,
            styles.headerRow,
            styles.lightGreyBackground,
            { borderBottom: "1px solid #E2E2E4" },
            {
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 2,
              paddingBottom: 2,
            },
          ]}
        >
          <View
            style={[
              styles.cell,
              { width: isSelfPayRate ? 220 : 90, textAlign: "left" },
            ]}
          >
            <Text>Provider</Text>
          </View>
          <View
            style={[
              styles.cell,
              { width: isSelfPayRate ? 250 : 130, textAlign: "left" },
            ]}
          >
            <Text>CPT Codes</Text>
          </View>
          {!isSelfPayRate && (
            <View style={[styles.cell, { width: 100, textAlign: "right" }]}>
              <Text>Insurance Rate</Text>
            </View>
          )}
          {!isSelfPayRate && (
            <View style={[styles.cell, { width: 100, textAlign: "right" }]}>
              <Text>Insurance Pays</Text>
            </View>
          )}
          <View style={[styles.cell, { width: 80, textAlign: "right" }]}>
            <Text>You Pay</Text>
          </View>
        </View>

        {/* Data Rows */}
        {breakdown.map((row, index) => (
          <View key={index} style={styles.row}>
            <View
              style={[
                styles.cell,
                {
                  width: isSelfPayRate ? 220 : 90,
                  textAlign: "left",
                  paddingTop: 2,
                  paddingBottom: 2,
                },
              ]}
            >
              <Text>
                {(SpecialtyListDictCheckout as any)[row.specialty] ||
                  row.specialty}
              </Text>
            </View>
            <View
              style={[
                styles.cell,
                {
                  width: isSelfPayRate ? 250 : 130,
                  textAlign: "left",
                  paddingTop: 2,
                  paddingBottom: 2,
                },
              ]}
            >
              <Text style={{ fontSize: 10 }}>
                {row.cptCodes?.join(", ") || ""}
              </Text>
            </View>
            {!isSelfPayRate && (
              <View
                style={[
                  styles.cell,
                  {
                    width: 100,
                    textAlign: "right",
                    paddingTop: 2,
                    paddingBottom: 2,
                  },
                ]}
              >
                <Text>{parsePrice(centsToDollars(row.insurance_rate))}</Text>
              </View>
            )}
            {!isSelfPayRate && (
              <View
                style={[
                  styles.cell,
                  {
                    width: 100,
                    textAlign: "right",
                    paddingTop: 2,
                    paddingBottom: 2,
                  },
                ]}
              >
                <Text>{parsePrice(centsToDollars(row.insurance_pays))}</Text>
              </View>
            )}
            <View
              style={[
                styles.cell,
                {
                  width: 80,
                  textAlign: "right",
                  paddingTop: 2,
                  paddingBottom: 2,
                },
              ]}
            >
              <Text>{parsePrice(centsToDollars(row.you_pay))}</Text>
            </View>
          </View>
        ))}

        {/* Total Row */}
        <View
          style={[
            styles.row,
            styles.headerRow,
            styles.lightGreyBackground,
            { borderBottom: "1px solid #E2E2E4" },
            {
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 2,
              paddingBottom: 2,
            },
          ]}
        >
          <View
            style={[
              styles.cell,
              { width: isSelfPayRate ? 220 : 90, textAlign: "left" },
            ]}
          >
            <Text>Total</Text>
          </View>
          <View
            style={[
              styles.cell,
              { width: isSelfPayRate ? 250 : 130, textAlign: "left" },
            ]}
          >
            <Text />
          </View>
          {!isSelfPayRate && (
            <View style={[styles.cell, { width: 100, textAlign: "right" }]}>
              <Text>{parsePrice(centsToDollars(totals.insurance_rate))}</Text>
            </View>
          )}
          {!isSelfPayRate && (
            <View style={[styles.cell, { width: 100, textAlign: "right" }]}>
              <Text>{parsePrice(centsToDollars(totals.insurance_pays))}</Text>
            </View>
          )}
          <View style={[styles.cell, { width: 80, textAlign: "right" }]}>
            <Text>{parsePrice(centsToDollars(totals.you_pay))}</Text>
          </View>
        </View>
      </View>

      {/* Non-Covered Procedures */}
      {nonCoveredProcedures.length > 0 && (
        <View
          style={[
            styles.payerContainer,
            { marginTop: 0, marginLeft: 50, marginRight: 50, marginBottom: 3 },
          ]}
        >
          <Text
            style={[
              styles.payerTitleBold,
              { color: "#d92d20", marginBottom: 8, fontFamily: "Helvetica" },
            ]}
          >
            Non-Covered Procedures
          </Text>
          <View style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {nonCoveredProcedures.map(
              (procedure: INonCoveredProcedure, index: number) => (
                <View
                  key={procedure.id || index}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingHorizontal: 5,
                    paddingRight: isSelfPayRate ? 5 : 10,
                  }}
                >
                  <Text style={[styles.smallText, { color: "#344054" }]}>
                    {procedure.cptDescription}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      { fontFamily: "Helvetica-Bold", color: "#17143f" },
                    ]}
                  >
                    {parsePrice(centsToDollars(procedure.amountInCents))}
                  </Text>
                </View>
              )
            )}
          </View>
        </View>
      )}
      <View
        style={[
          styles.payerContainer,
          {
            marginLeft: 50,
            marginRight: 50,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: isSelfPayRate ? 5 : 15,
            marginBottom: 0,
          },
        ]}
      >
        <Text
          style={[
            styles.payerTitleBold,
            { color: "#344054", gap: 0, marginBottom: 0 },
          ]}
        >
          Total Payment Due
        </Text>
        <Text
          style={[
            styles.payerTitleBold,
            { color: "#344054", gap: 0, marginBottom: 0 },
          ]}
        >
          {parsePrice(centsToDollars(clearPrice))}
        </Text>
      </View>
    </View>
  );
}
